import { useQuery } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";
import type { ClientDocumentRecord } from "./documents";
import type { ProcedureRecord } from "./procedures";
import type { LegalCaseRecord } from "./legal";
import type { InvoiceRecord } from "./invoices";

/**
 * Cliente de datos del Portal del cliente (Fase 19). Todos estos endpoints
 * (`/api/portal/*`) son de solo lectura y ya vienen forzados server-side al
 * clientId del propio usuario autenticado — nunca se envía ni acepta un
 * clientId desde el frontend.
 */

export interface PortalClientProfile {
  id: string;
  clientType: "PERSONA_NATURAL" | "PERSONA_JURIDICA";
  documentType: string;
  documentNumber: string;
  fullName: string | null;
  businessName: string | null;
  tradeName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  status: string;
  createdAt: string;
}

export function useMyProfile() {
  return useQuery({
    queryKey: ["portal", "me"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PortalClientProfile>>("/portal/me");
      return data.data;
    },
  });
}

export interface PortalListParams {
  page?: number;
  pageSize?: number;
}

export function useMyDocuments(params: PortalListParams = {}) {
  return useQuery({
    queryKey: ["portal", "documents", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ClientDocumentRecord[]>>("/portal/documents", { params });
      return data;
    },
  });
}

/** Obtiene una URL de descarga firmada (expira en minutos) para un documento propio. */
export async function downloadMyDocument(documentId: string): Promise<{ url: string; originalName: string }> {
  const { data } = await apiClient.get<ApiSuccessResponse<{ url: string; originalName: string; mimeType: string }>>(
    `/portal/documents/${documentId}/download`
  );
  return data.data;
}

export function useMyProcedures(params: PortalListParams = {}) {
  return useQuery({
    queryKey: ["portal", "procedures", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ProcedureRecord[]>>("/portal/procedures", { params });
      return data;
    },
  });
}

export function useMyLegalCases(params: PortalListParams = {}) {
  return useQuery({
    queryKey: ["portal", "legal-cases", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<LegalCaseRecord[]>>("/portal/legal-cases", { params });
      return data;
    },
  });
}

export function useMyInvoices(params: PortalListParams = {}) {
  return useQuery({
    queryKey: ["portal", "invoices", params],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<InvoiceRecord[]>>("/portal/invoices", { params });
      return data;
    },
  });
}

export function useMyInvoice(id: string | undefined) {
  return useQuery({
    queryKey: ["portal", "invoices", id],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<InvoiceRecord>>(`/portal/invoices/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export interface MyCarteraInvoiceSummary {
  id: string;
  invoiceNumber: number;
  total: number;
  balance: number;
  status: string;
  dueDate: string;
}

export interface MyCarteraSummary {
  totalOutstanding: number;
  invoices: MyCarteraInvoiceSummary[];
}

export function useMyCarteraSummary() {
  return useQuery({
    queryKey: ["portal", "cartera-summary"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<MyCarteraSummary>>("/portal/cartera-summary");
      return data.data;
    },
  });
}
